// Validação de CPF e busca de CEP (ViaCEP) — usados no cadastro rápido e no
// formulário público de preenchimento (RN-112g).

export const cpfDigits = (value: string): string => (value || '').replace(/\D/g, '');

export const formatCpf = (value: string): string => {
  const d = cpfDigits(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

export const isValidCpf = (value: string): boolean => {
  const d = cpfDigits(value);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;

  const check = (len: number): number => {
    let sum = 0;
    let weight = len + 1;
    for (let i = 0; i < len; i++) sum += Number(d[i]) * weight--;
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  return check(9) === Number(d[9]) && check(10) === Number(d[10]);
};

export const formatCep = (value: string): string => {
  const d = (value || '').replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

export interface AddressFromCep {
  street: string;
  district: string;
  city: string;
  state: string;
}

/** Consulta o ViaCEP (público, sem chave). Retorna null para CEP inexistente/incompleto. */
export const fetchAddressByCep = async (cep: string): Promise<AddressFromCep | null> => {
  const d = (cep || '').replace(/\D/g, '');
  if (d.length !== 8) return null;
  try {
    const response = await fetch(`https://viacep.com.br/ws/${d}/json/`);
    const data = await response.json();
    if (data?.erro) return null;
    return {
      street: data.logradouro || '',
      district: data.bairro || '',
      city: data.localidade || '',
      state: data.uf || '',
    };
  } catch {
    return null;
  }
};

/** Idade em anos completos (regra dos obrigatórios por idade). */
export const ageFromBirthDate = (iso: string): number | null => {
  if (!iso) return null;
  const birth = new Date(iso);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

/** Data de hoje em ISO (yyyy-mm-dd) — usada como `max` dos campos de nascimento. */
export const todayIso = (): string => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
};

/** Nascimento não pode ser posterior a hoje (hoje é válido — recém-nascido). */
export const isFutureBirthDate = (iso: string): boolean =>
  Boolean(iso) && iso > todayIso();

/** Formato de email — pragmático: pega erro de digitação, não implementa a RFC inteira. */
export const isValidEmail = (value: string): boolean => {
  const v = (value || '').trim();
  if (!v || v.length > 254) return false;
  return /^[^\s@.]+(\.[^\s@.]+)*@[^\s@.]+(\.[^\s@.]+)+$/.test(v);
};
